import React from 'react';
import { UserRound } from 'lucide-react';
import { FilterMultiSelect } from './FilterMultiSelect';

const AVATAR_COLORS = [
  'bg-blue-500',
  'bg-violet-500',
  'bg-emerald-500',
  'bg-amber-500',
  'bg-rose-500',
  'bg-cyan-500',
  'bg-indigo-500',
];

export type UserMultiSelectOption = {
  id: string;
  display_name: string | null;
  email: string;
};

export type UserMultiSelectProps = {
  users: UserMultiSelectOption[];
  selected: string[];
  onChange: (ids: string[]) => void;
  selectionMode?: 'emptyMeansAll' | 'explicit';
  assignLabel?: string;
  countLabel?: string;
  allLabel?: string;
  searchPlaceholder?: string;
  emptyListLabel?: string;
  showEmailSubtitle?: boolean;
  className?: string;
  triggerClassName?: string;
  width?: number;
  disabled?: boolean;
  panelAlign?: 'start' | 'end';
  ariaLabel?: string;
};

export function avatarColorClass(userId: string): string {
  let hash = 0;
  for (let i = 0; i < userId.length; i++) hash = (hash * 31 + userId.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
}

export function initialsFromUser(user: UserMultiSelectOption): string {
  const name = user.display_name?.trim();
  if (name) {
    const parts = name.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return name.slice(0, 2).toUpperCase();
  }
  const email = user.email.trim();
  if (!email) return '?';
  const local = email.split('@')[0] ?? email;
  return local.slice(0, 2).toUpperCase();
}

function userLabel(user: UserMultiSelectOption): string {
  return (user.display_name && user.display_name.trim()) || user.email || user.id;
}

export const PROJEKTLEITER_MULTISELECT_LABELS = {
  assignLabel: 'Projektleiter',
  countLabel: 'Projektleiter',
  allLabel: 'Alle Projektleiter',
  searchPlaceholder: 'Projektleiter suchen',
  emptyListLabel: 'Keine Projektleiter in der Instanz',
  ariaLabel: 'Projektleiter auswählen',
} as const;

export function UserMultiSelect({
  users,
  selected,
  onChange,
  selectionMode = 'explicit',
  assignLabel = 'Benutzer',
  countLabel = 'Benutzer',
  allLabel = 'Alle Benutzer',
  searchPlaceholder = 'Benutzer suchen',
  emptyListLabel = 'Keine Benutzer',
  showEmailSubtitle = true,
  className = '',
  triggerClassName = '',
  width = 320,
  disabled = false,
  panelAlign = 'end',
  ariaLabel = 'Benutzer auswählen',
}: UserMultiSelectProps) {
  const options = users.map((user) => {
    const label = userLabel(user);
    const subtitle =
      showEmailSubtitle && user.display_name?.trim() && user.email.trim()
        ? user.email.trim()
        : undefined;
    return {
      id: user.id,
      label,
      subtitle,
      leading: (
        <span
          className={`flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full text-[10px] font-bold text-white ${avatarColorClass(user.id)}`}
        >
          {initialsFromUser(user)}
        </span>
      ),
    };
  });

  return (
    <FilterMultiSelect
      options={options}
      selected={selected}
      onChange={onChange}
      selectionMode={selectionMode}
      allLabel={allLabel}
      countLabel={countLabel}
      assignLabel={assignLabel}
      searchPlaceholder={searchPlaceholder}
      emptyListLabel={emptyListLabel}
      showSubtitle={showEmailSubtitle}
      icon={<UserRound className="h-3.5 w-3.5" aria-hidden />}
      className={className}
      triggerClassName={triggerClassName}
      width={width}
      disabled={disabled}
      panelAlign={panelAlign}
      ariaLabel={ariaLabel}
    />
  );
}
